import { isStrictApiMockingEnabled } from '@/utils/runtime-mode.utils'
import { DEFAULT_APPEARANCE_JSON, DEFAULT_LANG_CONFIG } from '@/constants/chat-theme.constants'

const defaultChannel = {
  id: 'channel_demo_1',
  name: 'Web Chat',
  type: 'amio_chat'
}

// every channel that exists in the org, deployed or not - channels outlive their deployments
// (like the engine's org-wide channel listing), so undeploying one leaves it here as unbound
const orgChannels = [
  defaultChannel,
  { id: 'channel_demo_free_chat', name: 'Web Chat PL', type: 'amio_chat' },
  { id: 'channel_demo_whatsapp', name: 'WhatsApp CZ', type: 'whatsapp' },
  {
    id: 'channel_demo_email',
    name: 'Support inbox',
    type: 'email_daktela',
    daktela_url: 'https://company.daktela.com',
    access_token: 'demo-access-token'
  }
]

const knowledgeBaseExplorerResults = [
  {
    id: '2c6e72a5-e59c-09ec-95dd-5b606a58c487',
    content: null,
    score: 0.5,
    title: 'New Balance Fresh Foam X More v6 Reflection/ Blue Oyster/ Tangerine Heat',
    payload: {
      id: '2c6e72a5-e59c-09ec-95dd-5b606a58c487',
      score: 0.5,
      product_id: ['475633_10', '475633_10_5', '475633_11', '475633_8_5', '475633_9'],
      link: [
        'https://www.footshop.eu/en/mens-shoes/475633-new-balance-fresh-foam-x-more-v6-reflection-blue-oyster-tangerine-heat.html?size=28',
        'https://www.footshop.eu/en/mens-shoes/475633-new-balance-fresh-foam-x-more-v6-reflection-blue-oyster-tangerine-heat.html?size=29'
      ],
      size: ['42', '42.5', '44', '44.5', '45'],
      brand: 'New Balance',
      color: 'Reflection/ Blue Oyster/ Tangerine Heat',
      price: 170,
      gender: 'male',
      condition: 'new',
      sale_price: '127.49 EUR',
      availability: 'in stock',
      product_type: ["Men's", "Men's shoes", 'New Balance'],
      source_id: '7416779798989261312',
      tenant_id: '7356582791368951277'
    }
  },
  {
    id: 'f855cba5-618a-22ad-a413-59b7f7a33765',
    score: 0.25,
    title: 'Claims',
    content: 'Claims must include a copy of the invoice and completed claim form. Goods should be clean, securely packed, and sent within the warranty period.',
    payload: {
      id: 'f855cba5-618a-22ad-a413-59b7f7a33765',
      score: 0.25,
      content: 'Claims must include a copy of the invoice and completed claim form. Goods should be clean, securely packed, and sent within the warranty period.',
      title: 'Claims',
      source: 'https://www.footshop.eu/en/claims',
      parent_id: '7414608081684549520.json',
      chunk_index: 0,
      tenant_id: '7356582791368951277'
    }
  }
]

// PRO-671 agent settings, keyed by the tier the engine would store them at (multi-bot group, or the bot when
// it has no markets) so switching between market variants in isolated mode behaves like the real thing.
const agentSettings = {}

// Org-scoped chat themes (engine: /chat-themes), stored in the engine's snake_case response shape.
const chatThemes = [
  { id: 'theme_default', name: 'Default', theme_config: deepClone(DEFAULT_APPEARANCE_JSON), lang_config: deepClone(DEFAULT_LANG_CONFIG) }
]

// PRO-715: what POST /utils/site-identity answers in isolated mode — the PARTIAL theme the engine derives from a
// shop website (brand colour on every brand-coloured key, logo, header title). Deterministic so screenshots match.
const DEMO_BRAND = '#0a7c5a'
const DEMO_SITE_IDENTITY_PATCH = {
  theme_config: {
    header: { bg: DEMO_BRAND, text: '#fff', closeBtn: { color: '#fff' }, logoOnly: false,
      logoUrl: 'https://amio-testing.s3.eu-central-1.amazonaws.com/amio_logo.png' },
    launcher: { bg: DEMO_BRAND },
    sentMessage: {
      bg: DEMO_BRAND,
      text: '#fff',
      button: { bg: DEMO_BRAND, bgHover: 'rgba(10, 124, 90, 0.75)', text: '#fff', textHover: '#fff' },
      carouselButton: { text: DEMO_BRAND, textHover: '#fff', bgHover: DEMO_BRAND },
      qr: { borderColor: DEMO_BRAND, text: DEMO_BRAND, bgHover: DEMO_BRAND, textHover: '#fff' },
      file: { bg: 'rgba(10, 124, 90, 0.15)', text: DEMO_BRAND, border: DEMO_BRAND }
    },
    userInput: { sendIcon: '#fff', sendButton: DEMO_BRAND },
    statusInfo: { bg: DEMO_BRAND, text: '#fff' }
  },
  lang_config: { default: { welcome: 'Demo Shop' } }
}
const DEMO_SITE_IDENTITY_AGENT_SETTINGS = { system_agent_company: 'Demo Shop', system_agent_formality: 'informal' }

// PRO-719: a feed run that had to skip a few products (index_execution.stats, written by gpt-indexer)
const mockSkippedRunStats = {
  products: { total: 1240, indexed: 1234, skipped: 6 },
  skipped: [
    { sourceId: 'source_demo_xml', id: '346306|Black', name: 'Nike Air Force 1 \'07 Black', field: 'g:description', reason: 'too_long', tokens: 41230 },
    { sourceId: 'source_demo_xml', id: '475633|Reflection', name: 'New Balance Fresh Foam X More v6', field: 'g:description', reason: 'too_long', tokens: 18940 },
    { sourceId: 'source_demo_xml', id: 'GIFT-CARD-50', name: 'Gift card 50 EUR', field: 'PARAM', reason: 'too_long', tokens: 9120 },
    { sourceId: 'source_demo_xml', id: '512004|White', name: 'Adidas Samba OG', field: 'g:product_detail', reason: 'too_long', tokens: 8840 },
    { sourceId: 'source_demo_xml', id: 'SAMPLE-001', name: 'Sample item', field: null, reason: 'empty' },
    { sourceId: 'source_demo_xml', id: 'SAMPLE-002', name: 'Sample item 2', field: null, reason: 'empty' }
  ]
}

const state = {
  // Tools are org-scoped. `usedBy` mirrors what the engine derives by scanning conversation JSON —
  // it exists here only so the 409-while-referenced guard can be exercised without a database.
  tools: [
    {
      id: 'tool_order_status',
      name: 'get_order_status',
      description: 'Look up the delivery status of an order by its order number.',
      outcome_instructions: 'If status is DELIVERED, give the delivery date. If status is RETURN_REJECTED, explain the reason ' +
        'and offer an exchange. If the request fails, apologise and hand over to an agent.',
      parameters: {
        type: 'object',
        properties: {
          orderId: { type: 'string', description: 'The order number, e.g. AB-10293' }
        },
        required: ['orderId'],
        additionalProperties: false
      },
      url: 'https://${variable.shopApiDomain}/orders/${args.orderId}',
      http_verb: 'GET',
      headers: { Authorization: 'Bearer ${variable.shopApiKey}' },
      body: null,
      query_params: null,
      timeout_ms: 10000,
      usedBy: { answers: [{ id: 'a_demo_1', bot_id: 'bot_demo_1', name: 'Opening hours' }], events: [], templates: [] }
    },
    {
      id: 'tool_stock_check',
      name: 'check_stock',
      description: 'Check whether a product is in stock in a given size.',
      outcome_instructions: null,
      parameters: {
        type: 'object',
        properties: {
          sku: { type: 'string', description: 'Product SKU' },
          size: { type: 'string', description: 'Requested size, e.g. 42' }
        },
        required: ['sku'],
        additionalProperties: false
      },
      url: 'https://${variable.shopApiDomain}/stock',
      http_verb: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{"sku": "${args.sku}", "size": "${args.size}"}',
      query_params: null,
      timeout_ms: 8000,
      usedBy: { answers: [], events: [], templates: [] }
    }
  ],
  organization: {
    id: '7426530691171240939',
    name: 'Amio Local',
    is_active: true
  },
  bots: [
    {
      id: 'bot_demo_1',
      name: 'Footwear Bot',
      timezone: 'Europe/Prague',
      type: 'chat',
      language: 'en',
      country: 'GB',
      multi_bot_id: 'market_group_demo',
      market_id: 'market_en_demo',
      is_primary_variant: true,
      nlp_model: 'gpt',
      nlp_model_type: 'gpt',
      is_operator_available: false,
      unknown_intent: {
        intent_platform_id: 'unknown'
      }
    },
    {
      id: 'bot_demo_2',
      name: 'Footwear Bot',
      timezone: 'Europe/Zurich',
      type: 'chat',
      language: 'fr',
      country: 'CH',
      multi_bot_id: 'market_group_demo',
      market_id: 'market_fr_demo',
      primary_bot_id: 'bot_demo_1',
      nlp_model: 'gpt',
      nlp_model_type: 'gpt',
      is_operator_available: false,
      unknown_intent: {
        intent_platform_id: 'unknown'
      }
    }
  ],
  answersByBotId: {
    bot_demo_1: [
      {
        id: 'a_demo_1',
        bot_id: 'bot_demo_1',
        name: 'Opening hours',
        published: true,
        conversation_id: 'a_demo_1',
        conversation_json_hash: 'hash_demo_1',
        multi_bot_item_id: 'a_demo_1',
        conversation_json: {
          states: {
            root: {
              type: 'output',
              actions: ['action_1'],
              transitions: [
                {
                  state: 'ref:global:findIntent',
                  condition: {
                    input: 'userInput',
                    expression: { true: {} }
                  }
                }
              ]
            }
          },
          actions: {
            action_1: {
              type: 'output',
              content: {
                type: 'text',
                payload: 'Hello from isolated mode'
              }
            }
          },
          postbacks: {}
        },
        intent: {
          id: 'intent_demo_1',
          platform_id: 'intent_demo_1',
          description: 'hello'
        },
        request_outcome: {
          id: 'outcome_demo_1',
          name: 'AUTOMATED'
        }
      }
    ],
    bot_demo_2: [
      {
        id: 'a_demo_2',
        bot_id: 'bot_demo_2',
        name: 'Opening hours',
        published: false,
        conversation_id: 'a_demo_2',
        conversation_json_hash: 'hash_demo_2',
        multi_bot_item_id: 'a_demo_1',
        inherits_conversation: false,
        conversation_json: {
          states: {
            root: {
              type: 'output',
              actions: ['action_1'],
              transitions: [
                {
                  state: 'ref:global:findIntent',
                  condition: {
                    input: 'userInput',
                    expression: { true: {} }
                  }
                }
              ]
            }
          },
          actions: {
            action_1: {
              type: 'output',
              content: {
                type: 'text',
                payload: 'Bonjour du mode isolé'
              }
            }
          },
          postbacks: {}
        },
        intent: {
          id: 'intent_demo_2',
          platform_id: 'intent_demo_2',
          description: 'bonjour'
        },
        request_outcome: {
          id: 'outcome_demo_1',
          name: 'AUTOMATED'
        }
      }
    ]
  },
  markets: [
    {
      id: 'market_en_demo',
      name: 'UK - EN',
      language: 'en'
    },
    {
      id: 'market_fr_demo',
      name: 'Swiss - FR',
      language: 'fr'
    }
  ],
  indexGroups: [],
  indexes: [
    {
      id: 'index_demo_knowledge',
      name: 'Footshop UK - EN',
      index_group_id: null,
      market_id: 'market_en_demo',
      provider: 'qdrant',
      sources: [
        { id: 'source_demo_xml', type: 'url', format: 'xml', name: 'Footshop products' },
        { id: 'source_demo_web', type: 'web_scraping', format: 'html', name: 'Footshop help center' }
      ],
      provider_status: { status: 'success' },
      last_index_execution: { status: 'finished', created_on: '2026-08-17T12:00:00.000Z', stats: mockSkippedRunStats }
    }
  ],
  multiBots: [
    {
      id: 'market_group_demo',
      name: 'UK - EN',
      is_primary_variant: true,
      primary_bot_id: 'bot_demo_1',
      primary_bot: {
        id: 'bot_demo_1',
        multi_bot_id: 'market_group_demo',
        market_id: 'market_en_demo'
      }
    },
    {
      id: 'market_fr_demo',
      name: 'Swiss - FR',
      is_primary_variant: false,
      primary_bot_id: 'bot_demo_2',
      primary_bot: {
        id: 'bot_demo_2',
        multi_bot_id: 'market_group_demo',
        market_id: 'market_fr_demo'
      }
    }
  ],
  deploymentsByBotId: {
    bot_demo_1: [
      {
        channel: defaultChannel
      }
    ],
    bot_demo_2: [
      {
        channel: defaultChannel
      }
    ]
  },
  integrations: [],
  analyticsOutcomes: [
    { id: 'outcome_started', name: 'REQUEST_STARTED' },
    { id: 'outcome_yes', name: 'CUSTOMER_ANSWERED_YES' },
    { id: 'outcome_requested', name: 'AGENT_REQUESTED' },
    { id: 'outcome_partially', name: 'CUSTOMER_ANSWERED_PARTIALLY' }
  ],
  analyticsRequestOutcomesByBotId: {
    bot_demo_1: [
      {
        id: 'request_en_1',
        contact_id: 'contact_en_1',
        outcome_id: 'outcome_started',
        outcome_created_on: '2026-07-02T10:00:00.000Z',
        intent_platform_id: 'intent_demo_1',
        channel_id: defaultChannel.id
      },
      {
        id: 'request_en_1',
        contact_id: 'contact_en_1',
        outcome_id: 'outcome_yes',
        outcome_created_on: '2026-07-02T10:02:00.000Z',
        intent_platform_id: 'intent_demo_1',
        channel_id: defaultChannel.id
      },
      {
        id: 'request_en_2',
        contact_id: 'contact_en_2',
        outcome_id: 'outcome_started',
        outcome_created_on: '2026-07-02T11:00:00.000Z',
        intent_platform_id: 'intent_demo_1',
        channel_id: defaultChannel.id
      },
      {
        id: 'request_en_2',
        contact_id: 'contact_en_2',
        outcome_id: 'outcome_requested',
        outcome_created_on: '2026-07-02T11:03:00.000Z',
        intent_platform_id: 'intent_demo_1',
        channel_id: defaultChannel.id
      }
    ],
    bot_demo_2: [
      {
        id: 'request_fr_1',
        contact_id: 'contact_fr_1',
        outcome_id: 'outcome_started',
        outcome_created_on: '2026-07-02T12:00:00.000Z',
        intent_platform_id: 'intent_demo_2',
        channel_id: defaultChannel.id
      },
      {
        id: 'request_fr_1',
        contact_id: 'contact_fr_1',
        outcome_id: 'outcome_partially',
        outcome_created_on: '2026-07-02T12:04:00.000Z',
        intent_platform_id: 'intent_demo_2',
        channel_id: defaultChannel.id
      }
    ]
  },
  // pre-aggregated GET /analytics/user-feedback counts, one entry per bot/day/channel
  analyticsUserFeedbackByBotId: {
    bot_demo_1: [
      { created_on: '2026-07-01T12:00:00.000Z', channel_id: defaultChannel.id, positive: 64, negative: 12, notRated: 231 },
      { created_on: '2026-07-02T12:00:00.000Z', channel_id: defaultChannel.id, positive: 78, negative: 19, notRated: 257 }
    ],
    bot_demo_2: [
      { created_on: '2026-07-02T12:00:00.000Z', channel_id: defaultChannel.id, positive: 42, negative: 6, notRated: 124 }
    ]
  }
}

function parseUrlParts(rawUrl = '') {
  const parsed = new URL(rawUrl, 'http://localhost')
  return {
    path: parsed.pathname,
    query: parsed.searchParams
  }
}

function mergeQueryWithConfigParams(query, params = {}) {
  const mergedQuery = new URLSearchParams(query.toString())
  if (!params || typeof params !== 'object') return mergedQuery

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return

    mergedQuery.delete(key)
    if (Array.isArray(value)) {
      if (!value.length) return
      mergedQuery.set(key, value.join(','))
      return
    }

    mergedQuery.set(key, String(value))
  })

  return mergedQuery
}

function buildResponse(config, data, status = 200) {
  return {
    data,
    status,
    statusText: status >= 400 ? 'Error' : 'OK',
    headers: {},
    config,
    request: { mocked: true }
  }
}

function readPayload(config) {
  if (!config || config.data == null) return {}

  if (typeof config.data === 'string') {
    try {
      return JSON.parse(config.data)
    } catch (err) {
      return {}
    }
  }
  return config.data
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value))
}

function findMockTool(id) {
  return state.tools.find(tool => tool.id === id) || null
}

function toToolResponse(tool) {
  const response = { ...tool }
  // `usedBy` is mock bookkeeping for the delete guard — the real API never returns it
  delete response.usedBy
  const warnings = unusedParameterWarnings(tool)
  return warnings.length ? { ...response, warnings } : response
}

// The two engine-side validations that change what the user sees, reproduced so the mocked UI
// behaves like the real one. Everything else the engine validates simply cannot be triggered from
// the form.
function validateMockTool(config, tool) {
  const declared = Object.keys((tool.parameters && tool.parameters.properties) || {})
  const used = usedArgumentNames(tool)
  const undeclared = used.filter(name => !declared.includes(name))
  if (undeclared.length) {
    throw buildError(config, `The request uses \${args.${undeclared.join('}, \${args.')}} but that parameter is not declared`, 422)
  }
}

function unusedParameterWarnings(tool) {
  const declared = Object.keys((tool.parameters && tool.parameters.properties) || {})
  const used = usedArgumentNames(tool)
  const unused = declared.filter(name => !used.includes(name))
  return unused.length ? [`Declared but never used in the request: ${unused.join(', ')}`] : []
}

function usedArgumentNames(tool) {
  const searchable = JSON.stringify([tool.url, tool.body, tool.headers, tool.query_params])
  const matches = searchable.match(/\$\{args\.[^}]+\}/g) || []
  return [...new Set(matches.map(match => match.slice('${args.'.length, -1)))]
}

// Stands in for a real HTTP call. Substitutes the same way the engine does so the echoed request is
// honest, then returns a canned body — there is no server to call in isolated mode.
function runMockTool(config, tool, payload) {
  const args = (payload && payload.args) || {}
  const required = (tool.parameters && tool.parameters.required) || []
  const missing = required.filter(name => !args[name])
  if (missing.length) throw buildError(config, `Fill in ${missing.join(', ')} — it is required`, 422)

  const declared = Object.keys((tool.parameters && tool.parameters.properties) || {})
  const unknown = Object.keys(args).filter(name => !declared.includes(name))
  if (unknown.length) throw buildError(config, `${unknown.join(', ')} is not a parameter of this tool`, 422)

  const substitute = text => (text || '').replace(/\$\{(args|variable)\.([^}]+)\}/g, (match, kind, name) =>
    kind === 'args' ? (args[name] || '') : `demo-${name}`)

  const substituteMap = map => Object.fromEntries(
    Object.entries(map || {}).map(([key, value]) => [substitute(key), substitute(String(value))])
  )

  return {
    request: {
      method: tool.http_verb,
      url: encodeURI(substitute(tool.url)),
      headers: substituteMap(tool.headers),
      query_params: substituteMap(tool.query_params),
      body: tool.body ? substitute(tool.body) : null
    },
    response: {
      http_status: 200,
      body: { ok: true, note: 'Mocked response — isolated mode does not make real HTTP calls.', received: args },
      duration_ms: 42,
      error: false
    }
  }
}

function createMockTool(config, payload) {
  const tool = { ...payload, id: `tool_${Date.now()}`, usedBy: { answers: [], events: [], templates: [] } }
  validateMockTool(config, tool)
  if (state.tools.some(candidate => candidate.name === tool.name)) {
    throw buildError(config, `Tool with the name "${tool.name}" already exists in this organization`, 409)
  }
  state.tools.push(tool)
  return toToolResponse(tool)
}

function updateMockTool(config, tool, payload) {
  const merged = { ...tool, ...payload }
  validateMockTool(config, merged)
  if (state.tools.some(candidate => candidate.id !== tool.id && candidate.name === merged.name)) {
    throw buildError(config, `Tool with the name "${merged.name}" already exists in this organization`, 409)
  }
  state.tools = state.tools.map(candidate => candidate.id === tool.id ? merged : candidate)
  return toToolResponse(merged)
}

function buildError(config, message, status = 500) {
  const error = new Error(message)
  error.config = config
  error.response = {
    status,
    data: {
      errors: [{ message }]
    },
    config
  }
  return error
}

function findBot(botId) {
  return state.bots.find(bot => bot.id === botId)
}

function listBots() {
  return state.bots.map(bot => ({ ...bot }))
}

function listMultiBots() {
  return state.multiBots.map(multiBot => deepClone(multiBot))
}

function listMarkets() {
  return state.markets.map(market => ({ ...market }))
}

function listIndexGroups() {
  return state.indexGroups.map(indexGroup => ({ ...indexGroup }))
}

function createIndexGroup(payload = {}) {
  const indexGroup = {
    id: `index_group_${Date.now()}_${state.indexGroups.length}`,
    name: payload.name || 'New Knowledge Base Group'
  }
  state.indexGroups.push(indexGroup)
  return { ...indexGroup }
}

// cross-org admin Indexation Overview; honours the same orgId / status filters as the engine
function listAdminIndexExecutions(query = {}) {
  const executions = [
    {
      id: 'exec_demo_running', index_id: 'index_demo_knowledge', index_name: 'Footshop UK - EN', organization_id: '7426530691171240939',
      organization_name: 'Footshop', status: 'running', created_on: '2026-08-18T09:55:00.000Z', finished_on: null, error: null, stats: null
    },
    {
      id: 'exec_demo_skipped', index_id: 'index_demo_knowledge', index_name: 'Footshop UK - EN', organization_id: '7426530691171240939',
      organization_name: 'Footshop', status: 'finished', created_on: '2026-08-17T12:00:00.000Z', finished_on: '2026-08-17T12:14:00.000Z',
      error: null, stats: mockSkippedRunStats
    },
    {
      id: 'exec_demo_clean', index_id: 'index_demo_cz', index_name: 'Footshop CZ - CS', organization_id: '7426530691171240939',
      organization_name: 'Footshop', status: 'finished', created_on: '2026-08-17T08:00:00.000Z', finished_on: '2026-08-17T08:09:00.000Z',
      error: null, stats: { products: { total: 980, indexed: 980, skipped: 0 }, skipped: [] }
    },
    {
      id: 'exec_demo_error', index_id: 'index_demo_other', index_name: 'Acme docs', organization_id: '7000000000000000001',
      organization_name: 'Acme', status: 'error', created_on: '2026-08-16T08:00:00.000Z', finished_on: '2026-08-16T08:01:00.000Z',
      error: { message: '0 of 1 sources indexed, 1 failed. docs feed: No items found in the feed for item tag "SHOPITEM".' }, stats: null
    }
  ]
  return deepClone(executions.filter(execution => (!query.orgId || execution.organization_id === query.orgId) &&
    (!query.status || execution.status === query.status)))
}

function listIndexes() {
  return state.indexes.map(index => deepClone(index))
}

function createIndex(payload = {}) {
  const createdAt = new Date().toISOString()
  const indexId = `index_${Date.now()}_${state.indexes.length}`
  const index = {
    ...deepClone(payload),
    id: indexId,
    name: payload.name || 'New Knowledge Base',
    index_group_id: payload.index_group_id || null,
    market_id: payload.market_id || null,
    provider: payload.provider || 'qdrant',
    sources: (payload.sources || []).map((source, position) => ({
      ...source,
      id: source.id || `${indexId}_source_${position}`
    })),
    provider_status: { status: 'success' },
    last_index_execution: {
      status: 'finished',
      created_on: createdAt
    }
  }
  state.indexes.push(index)
  return deepClone(index)
}

function searchKnowledgeBase(indexId, payload = {}) {
  const index = state.indexes.find(item => item.id === indexId)
  if (!index) return null

  const topN = Number.isInteger(payload.top_n) && payload.top_n > 0 ? payload.top_n : 10
  const response = {
    query: payload.query || '',
    provider: index.provider,
    results: deepClone(knowledgeBaseExplorerResults.slice(0, topN))
  }

  if (payload.mode === 'rag') {
    Object.assign(response, {
      reformulated_query: payload.query || '',
      filterable_fields: [
        { field: 'brand', type: 'keyword', collection: 'products', values: ['New Balance'] }
      ],
      proposed_filters: [{ field: 'brand', value: 'New Balance' }],
      applied_filters: { must: [{ key: 'brand', match: { value: 'New Balance' } }] },
      fell_back: false
    })
  }

  return response
}

function listAnswers(botId) {
  return (state.answersByBotId[botId] || []).map(answer => deepClone(answer))
}

function getAnswer(botId, answerId) {
  const answers = state.answersByBotId[botId] || []
  const answer = answers.find(item => item.id === answerId)
  return answer ? deepClone(answer) : null
}

function listAnswerVariants(botId, answerId) {
  const sourceAnswer = getAnswer(botId, answerId)
  if (!sourceAnswer) return []

  const multiBotItemId = sourceAnswer.multi_bot_item_id || sourceAnswer.id

  return Object.values(state.answersByBotId)
    .flat()
    .filter(answer => (answer.multi_bot_item_id || answer.id) === multiBotItemId)
    .map(answer => deepClone(answer))
}

function updateAnswer(botId, answerId, patchData = {}) {
  const answers = state.answersByBotId[botId] || []
  const index = answers.findIndex(item => item.id === answerId)
  if (index === -1) return null

  const answer = answers[index]
  const updated = { ...answer }

  if (patchData.name !== undefined) updated.name = patchData.name
  if (patchData.published !== undefined) updated.published = patchData.published
  if (patchData.conversation_json !== undefined) {
    updated.conversation_json = deepClone(patchData.conversation_json)
    updated.conversation_json_hash = patchData.conversation_json_hash || `hash_${Date.now()}`
  }

  answers[index] = updated
  state.answersByBotId[botId] = answers
  return deepClone(updated)
}

function createAnswer(botId, payload = {}) {
  const id = `a_demo_${Date.now()}`
  const intentId = `intent_demo_${Date.now()}`
  const conversationJson = payload.conversation_json || {
    states: {},
    actions: {},
    postbacks: {}
  }

  const answer = {
    id,
    name: payload.name || 'New Answer',
    published: false,
    conversation_id: id,
    conversation_json_hash: `hash_${Date.now()}`,
    conversation_json: conversationJson,
    intent: {
      id: intentId,
      platform_id: intentId,
      description: ''
    },
    request_outcome: {
      id: 'outcome_demo_1',
      name: 'AUTOMATED'
    }
  }

  const answers = state.answersByBotId[botId] || []
  answers.unshift(answer)
  state.answersByBotId[botId] = answers
  return deepClone(answer)
}

function getIntent(intentId) {
  for (const answers of Object.values(state.answersByBotId)) {
    const answer = answers.find(item => item.intent?.id === intentId)
    if (answer) return deepClone(answer.intent)
  }
  return null
}

function updateIntent(intentId, patchData = {}) {
  for (const botId of Object.keys(state.answersByBotId)) {
    const answers = state.answersByBotId[botId]
    const index = answers.findIndex(item => item.intent?.id === intentId)
    if (index === -1) continue

    const updatedIntent = {
      ...answers[index].intent
    }
    if (patchData.description !== undefined) updatedIntent.description = patchData.description

    answers[index] = {
      ...answers[index],
      intent: updatedIntent
    }
    state.answersByBotId[botId] = answers
    return deepClone(updatedIntent)
  }
  return null
}

function upsertBot(botId, patchData = {}) {
  const index = state.bots.findIndex(bot => bot.id === botId)
  if (index === -1) return null
  state.bots[index] = { ...state.bots[index], ...patchData }
  return { ...state.bots[index] }
}

function removeBot(botId) {
  const index = state.bots.findIndex(bot => bot.id === botId)
  if (index === -1) return false
  state.bots.splice(index, 1)
  delete state.deploymentsByBotId[botId]
  delete state.answersByBotId[botId]
  return true
}

function createBotFromPayload(payload = {}) {
  const primaryVariant = payload.primary_variant || {}
  const timezone = primaryVariant.timezone || payload.timezone || 'Europe/Prague'
  const type = primaryVariant.type || payload.type || 'chat'
  const language = primaryVariant.language || payload.language || 'en'
  const nlpModelType = primaryVariant.nlp_model_type || payload.nlp_model_type || 'gpt'
  const id = `bot_${Date.now()}`
  const bot = {
    id,
    name: payload.name || 'New Bot',
    timezone,
    type,
    language,
    country: primaryVariant.country || payload.country,
    nlp_model: nlpModelType,
    nlp_model_type: nlpModelType
  }
  state.bots.unshift(bot)
  state.deploymentsByBotId[id] = []
  return { ...bot }
}

function createClonedBot(sourceBot, payload = {}) {
  const id = `bot_${Date.now()}`
  const multiBotId = `multi_bot_${Date.now()}`
  const bot = {
    id,
    name: payload.name || `${sourceBot.name} copy`,
    timezone: payload.timezone || sourceBot.timezone || 'Europe/Prague',
    type: sourceBot.type || 'chat',
    language: payload.language || sourceBot.language || 'en',
    country: sourceBot.country,
    nlp_model: payload.nlp_model_type || sourceBot.nlp_model || 'gpt',
    nlp_model_type: payload.nlp_model_type || sourceBot.nlp_model_type || 'gpt',
    market_id: payload.market_id,
    multi_bot_id: multiBotId,
    is_primary_variant: true,
    primary_bot_id: id
  }
  state.bots.unshift(bot)
  state.deploymentsByBotId[id] = deepClone(state.deploymentsByBotId[sourceBot.id] || [])
  if (state.answersByBotId[sourceBot.id]) {
    state.answersByBotId[id] = deepClone(state.answersByBotId[sourceBot.id])
  }

  state.multiBots.push({
    id: multiBotId,
    name: payload.market_name || payload.name || bot.name,
    is_primary_variant: true,
    primary_bot_id: id,
    primary_bot: deepClone(bot)
  })
  return { ...bot }
}

function convertBotToMultiBot(payload = {}) {
  const botId = payload.primary_variant?.bot_id
  const marketId = payload.primary_variant?.market_id
  const bot = state.bots.find(item => item.id === botId)
  if (!bot) {
    const err = new Error(`Cannot use bot with id "${botId}". This bot does not exist in the organization.`)
    err.response = { status: 422, data: { message: err.message } }
    throw err
  }
  if (bot.multi_bot_id) {
    const err = new Error(`Cannot use bot with id "${botId}". This bot is already associated with a multi-bot.`)
    err.response = { status: 422, data: { message: err.message } }
    throw err
  }

  const multiBotId = `multi_bot_${Date.now()}`
  bot.multi_bot_id = multiBotId
  bot.market_id = marketId
  bot.is_primary_variant = true
  bot.primary_bot_id = botId

  const multiBot = {
    id: multiBotId,
    name: payload.name || bot.name,
    is_primary_variant: true,
    primary_bot_id: botId,
    primary_bot: deepClone(bot)
  }
  state.multiBots.push(multiBot)
  return deepClone(multiBot)
}

function listDeployments(botId) {
  return (state.deploymentsByBotId[botId] || []).map(item => ({ ...item }))
}

function listIntegrations() {
  return state.integrations.map(integration => deepClone(integration))
}

function createIntegration(botId, payload = {}) {
  const channels = listDeployments(botId).map(({ channel }) => ({
    id: channel.id,
    name: channel.name,
    type: channel.type
  }))
  const integration = {
    ...deepClone(payload),
    id: `integration_${Date.now()}_${state.integrations.length}`,
    bot_id: botId,
    channels
  }
  state.integrations.push(integration)
  return deepClone(integration)
}

function updateIntegration(integrationId, payload = {}) {
  const integration = state.integrations.find(item => item.id === integrationId)
  if (!integration) return null
  Object.assign(integration, deepClone(payload))
  return deepClone(integration)
}

function deleteIntegration(botId, integrationId) {
  const index = state.integrations.findIndex(item => item.id === integrationId && item.bot_id === botId)
  if (index === -1) return false
  state.integrations.splice(index, 1)
  return true
}

function listAnalyticsRequestOutcomes(botIds = [], {
  channelIds = [],
  dateFrom = null,
  dateTo = null
} = {}) {
  const selectedBotIds = botIds.length ? botIds : state.bots.map(({ id }) => id)
  const selectedChannelIds = new Set(channelIds)
  const fromTimestamp = dateFrom ? +new Date(dateFrom) : null
  const toTimestamp = dateTo ? +new Date(dateTo) : null

  return selectedBotIds
    .flatMap(botId => state.analyticsRequestOutcomesByBotId[botId] || [])
    .filter(requestOutcome => {
      const timestamp = +new Date(requestOutcome.outcome_created_on)
      if (selectedChannelIds.size && !selectedChannelIds.has(requestOutcome.channel_id)) return false
      if (fromTimestamp && timestamp < fromTimestamp) return false
      if (toTimestamp && timestamp > toTimestamp) return false
      return true
    })
    .sort((a, b) => {
      if (a.id === b.id) {
        return +new Date(a.outcome_created_on) - +new Date(b.outcome_created_on)
      }

      return String(a.id).localeCompare(String(b.id))
    })
    .map(requestOutcome => deepClone(requestOutcome))
}

function sumAnalyticsUserFeedback(botIds = [], { channelIds = [], dateFrom = null, dateTo = null } = {}) {
  const fromTimestamp = dateFrom ? +new Date(dateFrom) : -Infinity
  const toTimestamp = dateTo ? +new Date(dateTo) : Infinity

  return botIds
    .flatMap(botId => state.analyticsUserFeedbackByBotId[botId] || [])
    .filter(({ created_on, channel_id }) => {
      const timestamp = +new Date(created_on)
      return timestamp >= fromTimestamp && timestamp <= toTimestamp && (!channelIds.length || channelIds.includes(channel_id))
    })
    .reduce((sum, { positive, negative, notRated }) => ({
      positive: sum.positive + positive,
      negative: sum.negative + negative,
      notRated: sum.notRated + notRated
    }), { positive: 0, negative: 0, notRated: 0 })
}

function listAnalyticsOutcomes(requestOutcomes = []) {
  const outcomeIds = new Set(requestOutcomes.map(({ outcome_id }) => outcome_id))
  return state.analyticsOutcomes
    .filter(({ id }) => outcomeIds.has(id))
    .map(outcome => ({ ...outcome }))
}

function listOrgChannels() {
  return orgChannels.map(channel => ({ ...channel }))
}

function addDeployment(botId, channelId) {
  const list = state.deploymentsByBotId[botId] || []
  const exists = list.some(item => item.channel.id === channelId)
  if (!exists) {
    let channel = orgChannels.find(item => item.id === channelId)
    if (!channel) {
      channel = { id: channelId, name: `Channel ${channelId}`, type: 'amio_chat' }
      orgChannels.push(channel)
    }
    list.push({ channel: { ...channel } })
  }
  state.deploymentsByBotId[botId] = list
  return listDeployments(botId)
}

function createDeployment(botId, payload = {}) {
  const channelId = `channel_${Date.now()}`
  const channelName = payload.name || `Channel ${channelId}`
  const channelType = payload.type || 'amio_chat'
  const list = state.deploymentsByBotId[botId] || []
  const channel = { id: channelId, name: channelName, type: channelType }
  if (channelType === 'email_daktela') {
    channel.daktela_url = payload.daktela_url
    channel.access_token = payload.access_token
  }
  orgChannels.push(channel)
  const deployment = { channel: { ...channel } }
  list.push(deployment)
  state.deploymentsByBotId[botId] = list
  return { ...deployment }
}

function removeDeployment(botId, channelId) {
  const list = state.deploymentsByBotId[botId] || []
  state.deploymentsByBotId[botId] = list.filter(item => item.channel.id !== channelId)
}

function handleOrgApi(config, method, path) {
  if (method === 'get' && path === '/organizations') {
    return buildResponse(config, state.organization)
  }
  return null
}

function handleEngineApi(config, method, path, query) {
  if (method === 'post' && path === '/utils/daktela-email-details') {
    if (readPayload(config).access_token === 'invalid') {
      throw buildError(config, 'Daktela rejected the access token. Check the URL and token and try again.', 422)
    }
    return buildResponse(config, {
      queues: [
        { id: '1001', name: 'Support' },
        { id: '2001', name: 'Billing' }
      ],
      queue_statuses: [
        { id: 'open', name: 'Open' },
        { id: 'closed', name: 'Closed' }
      ],
      ticket_categories: [
        { id: 'cat1', name: 'Question' },
        { id: 'cat2', name: 'Refund' }
      ]
    })
  }

  if (method === 'get' && path === '/utils/plain-chat-identity') {
    return buildResponse(config, {})
  }

  if (method === 'post' && path === '/utils/bug-report') {
    return buildResponse(config, {
      thread_id: 'th_mock',
      note: 'Mocked response — isolated mode does not create a real bug report.'
    }, 201)
  }

  if (method === 'post' && path === '/utils/test-api-call') {
    const payload = readPayload(config)
    return buildResponse(config, {
      http_status: 200,
      body: {
        ok: true,
        note: 'Mocked response — isolated mode does not make real HTTP calls.',
        received: payload
      },
      headers: { 'content-type': 'application/json' },
      error: false
    })
  }

  if (method === 'get' && path === '/utils/preview-xml-feed') {
    const itemTag = query.get('itemTag') || 'Product'
    return buildResponse(config, {
      item_tag: itemTag,
      item_tag_data: {
        Product_ID: '7631',
        Name: 'On Cloud Nine Pillow',
        Brand: 'FabDecor',
        SKU: 'HEH-9133',
        Price: '24.99'
      }
    })
  }

  if (method === 'get' && path === '/analytics/request-outcomes') {
    const botIds = (query.get('botIds') || '').split(',').filter(Boolean)
    const channelIds = (query.get('channelIds') || '').split(',').filter(Boolean)
    const requestOutcomes = listAnalyticsRequestOutcomes(botIds, {
      channelIds,
      dateFrom: query.get('dateFrom'),
      dateTo: query.get('dateTo')
    })

    return buildResponse(config, {
      requestOutcomes,
      outcomes: listAnalyticsOutcomes(requestOutcomes)
    })
  }

  if (method === 'get' && path === '/analytics/user-feedback') {
    return buildResponse(config, sumAnalyticsUserFeedback((query.get('botIds') || '').split(',').filter(Boolean), {
      channelIds: (query.get('channelIds') || '').split(',').filter(Boolean),
      dateFrom: query.get('dateFrom'),
      dateTo: query.get('dateTo')
    }))
  }

  if (method === 'get' && path === '/bots') {
    return buildResponse(config, listBots())
  }

  if (method === 'get' && path === '/integrations') {
    return buildResponse(config, listIntegrations())
  }

  const createIntegrationMatch = path.match(/^\/bots\/([^/]+)\/integrations$/)
  if (createIntegrationMatch && method === 'post') {
    const botId = createIntegrationMatch[1]
    if (!findBot(botId)) throw buildError(config, `Mock bot not found: ${botId}`, 404)
    if (!listDeployments(botId).length) {
      throw buildError(config, `No deployed channels found for bot ${botId}. Please deploy the bot to at least one channel before creating an integration.`, 422)
    }
    return buildResponse(config, createIntegration(botId, readPayload(config)), 201)
  }

  const integrationDetailMatch = path.match(/^\/bots\/([^/]+)\/integrations\/([^/]+)$/)
  if (integrationDetailMatch) {
    const botId = integrationDetailMatch[1]
    const integrationId = integrationDetailMatch[2]
    if (method === 'patch') {
      const updated = updateIntegration(integrationId, { ...readPayload(config), bot_id: botId })
      if (!updated) throw buildError(config, `Mock integration not found: ${integrationId}`, 404)
      return buildResponse(config, updated)
    }
    if (method === 'delete') {
      const deleted = deleteIntegration(botId, integrationId)
      if (!deleted) throw buildError(config, `Mock integration not found: ${integrationId}`, 404)
      return buildResponse(config, {}, 204)
    }
  }

  if (method === 'get' && path === '/variables') {
    return buildResponse(config, [])
  }

  if (path === '/tools') {
    if (method === 'get') return buildResponse(config, state.tools.map(toToolResponse))
    if (method === 'post') return buildResponse(config, createMockTool(config, readPayload(config)), 201)
  }

  const toolTestMatch = path.match(/^\/tools\/([^/]+)\/test$/)
  if (toolTestMatch && method === 'post') {
    const tool = findMockTool(toolTestMatch[1])
    if (!tool) throw buildError(config, `Mock tool not found: ${toolTestMatch[1]}`, 404)
    return buildResponse(config, runMockTool(config, tool, readPayload(config)))
  }

  const toolReferencesMatch = path.match(/^\/tools\/([^/]+)\/references$/)
  if (toolReferencesMatch && method === 'get') {
    const tool = findMockTool(toolReferencesMatch[1])
    if (!tool) throw buildError(config, `Mock tool not found: ${toolReferencesMatch[1]}`, 404)
    return buildResponse(config, tool.usedBy)
  }

  const toolMatch = path.match(/^\/tools\/([^/]+)$/)
  if (toolMatch) {
    const tool = findMockTool(toolMatch[1])
    if (!tool) throw buildError(config, `Mock tool not found: ${toolMatch[1]}`, 404)

    if (method === 'get') return buildResponse(config, toToolResponse(tool))
    if (method === 'patch') return buildResponse(config, updateMockTool(config, tool, readPayload(config)))
    if (method === 'delete') {
      const { answers, events, templates } = tool.usedBy
      if (answers.length || events.length || templates.length) {
        throw buildError(config, 'Tool is being used in a conversation!', 409)
      }
      state.tools = state.tools.filter(candidate => candidate.id !== tool.id)
      return buildResponse(config, null, 204)
    }
  }

  if (method === 'get' && path === '/deployments') {
    const deployments = Object.keys(state.deploymentsByBotId).reduce((byBot, botId) => {
      const botDeployments = listDeployments(botId)
      if (botDeployments.length > 0) byBot[botId] = botDeployments
      return byBot
    }, {})
    return buildResponse(config, { deployments, failedBotIds: [], channels: listOrgChannels() })
  }

  const intentMatch = path.match(/^\/intents\/([^/]+)$/)
  if (intentMatch) {
    const intentId = intentMatch[1]
    if (method === 'get') {
      const intent = getIntent(intentId)
      if (!intent) throw buildError(config, `Mock intent not found: ${intentId}`, 404)
      return buildResponse(config, intent)
    }
    if (method === 'patch') {
      const updatedIntent = updateIntent(intentId, readPayload(config))
      if (!updatedIntent) throw buildError(config, `Mock intent not found: ${intentId}`, 404)
      return buildResponse(config, updatedIntent)
    }
  }

  const botMatch = path.match(/^\/bots\/([^/]+)$/)
  if (botMatch) {
    const botId = botMatch[1]
    if (method === 'get') {
      const bot = findBot(botId)
      if (!bot) throw buildError(config, `Mock bot not found: ${botId}`, 404)
      return buildResponse(config, { ...bot })
    }
    if (method === 'patch') {
      const updated = upsertBot(botId, readPayload(config))
      if (!updated) throw buildError(config, `Mock bot not found: ${botId}`, 404)
      return buildResponse(config, updated)
    }
    if (method === 'delete') {
      if (!removeBot(botId)) throw buildError(config, `Mock bot not found: ${botId}`, 404)
      return buildResponse(config, {}, 204)
    }
  }

  if (method === 'get' && path === '/markets') {
    return buildResponse(config, listMarkets())
  }

  if (method === 'get' && path === '/index-groups') {
    return buildResponse(config, listIndexGroups())
  }

  if (method === 'post' && path === '/index-groups') {
    return buildResponse(config, createIndexGroup(readPayload(config)), 201)
  }

  if (method === 'get' && path === '/indexes') {
    return buildResponse(config, listIndexes())
  }

  if (method === 'get' && path === '/admin/index-executions') {
    return buildResponse(config, listAdminIndexExecutions(query))
  }

  const indexSearchMatch = path.match(/^\/indexes\/([^/]+)\/search$/)
  if (indexSearchMatch && method === 'post') {
    const result = searchKnowledgeBase(indexSearchMatch[1], readPayload(config))
    if (!result) throw buildError(config, `Mock knowledge base not found: ${indexSearchMatch[1]}`, 404)
    return buildResponse(config, result)
  }

  if (method === 'post' && path === '/indexes') {
    return buildResponse(config, createIndex(readPayload(config)), 201)
  }

  if (method === 'post' && path === '/markets') {
    const payload = readPayload(config)
    return buildResponse(config, {
      id: `market_${Date.now()}`,
      name: payload.name,
      language: payload.language
    }, 201)
  }

  if (method === 'post' && path === '/multi-bots') {
    const payload = readPayload(config)
    if (payload.primary_variant?.bot_id) {
      try {
        return buildResponse(config, convertBotToMultiBot(payload), 201)
      } catch (err) {
        throw buildError(config, err.message, 422)
      }
    }
    // Same shape as the engine: the multi-bot with its primary bot, which SettingsBotCreate reads.
    const bot = createBotFromPayload(payload)
    const multiBotId = `multi_bot_${Date.now()}`
    Object.assign(findBot(bot.id), { multi_bot_id: multiBotId, market_id: payload.primary_variant?.market_id, is_primary_variant: true })
    const multiBot = { id: multiBotId, name: payload.name || bot.name, is_primary_variant: true, primary_bot_id: bot.id, primary_bot: deepClone(findBot(bot.id)) }
    state.multiBots.push(multiBot)
    // PRO-715: the engine presets blank agent settings from the website in the background; here it is instant.
    if (payload.website) agentSettings[multiBotId] = { ...DEMO_SITE_IDENTITY_AGENT_SETTINGS }
    return buildResponse(config, deepClone(multiBot), 201)
  }

  if (method === 'get' && path === '/bot-templates') {
    return buildResponse(config, [
      { id: 'cz-ecommerce', name: 'E-commerce (CZ)', bot_language: 'cs', bot_timezone: 'Europe/Prague', bot_type: 'chat', variables_prefix: 'shop' },
      { id: 'en-ecommerce', name: 'E-commerce (EN)', bot_language: 'en', bot_timezone: 'Europe/London', bot_type: 'chat', variables_prefix: 'shop' }
    ])
  }

  // PRO-715: theme settings "Generate from website".
  if (method === 'post' && path === '/utils/site-identity') {
    if (!readPayload(config).website) throw buildError(config, 'Website is required.', 422)
    return buildResponse(config, deepClone(DEMO_SITE_IDENTITY_PATCH))
  }

  if (path === '/chat-themes') {
    if (method === 'get') return buildResponse(config, deepClone(chatThemes))
    if (method === 'post') {
      const { name, themeConfig, langConfig } = readPayload(config)
      const theme = { id: `theme_${Date.now()}`, name: name || 'New theme', theme_config: themeConfig || {}, lang_config: langConfig || {} }
      chatThemes.push(theme)
      return buildResponse(config, deepClone(theme), 201)
    }
  }

  const chatThemeMatch = path.match(/^\/chat-themes\/([^/]+)$/)
  if (chatThemeMatch) {
    const index = chatThemes.findIndex(theme => theme.id === chatThemeMatch[1])
    if (index === -1) throw buildError(config, 'Chat theme not found', 404)
    if (method === 'delete') {
      chatThemes.splice(index, 1)
      return buildResponse(config, null, 204)
    }
    if (method === 'patch') {
      const { name, themeConfig, langConfig } = readPayload(config)
      chatThemes[index] = {
        ...chatThemes[index],
        ...(name ? { name } : {}),
        ...(themeConfig ? { theme_config: themeConfig } : {}),
        ...(langConfig ? { lang_config: langConfig } : {})
      }
    }
    return buildResponse(config, deepClone(chatThemes[index]))
  }

  if (method === 'get' && path === '/multi-bots') {
    return buildResponse(config, listMultiBots())
  }

  const cloneMatch = path.match(/^\/bots\/([^/]+)\/clone$/)
  if (cloneMatch && method === 'post') {
    const sourceBotId = cloneMatch[1]
    const sourceBot = findBot(sourceBotId)
    if (!sourceBot) throw buildError(config, `Mock source bot not found: ${sourceBotId}`, 404)

    const payload = readPayload(config)
    return buildResponse(config, createClonedBot(sourceBot, payload), 201)
  }

  const deploymentsMatch = path.match(/^\/bots\/([^/]+)\/deployments$/)
  if (deploymentsMatch) {
    const botId = deploymentsMatch[1]
    if (method === 'get') return buildResponse(config, listDeployments(botId))
    if (method === 'post') {
      const payload = readPayload(config)
      return buildResponse(config, createDeployment(botId, payload), 201)
    }
    if (method === 'put') {
      const payload = readPayload(config)
      const channelId = payload.channel_id
      if (!channelId) throw buildError(config, 'Mock deployment requires channel_id', 400)
      return buildResponse(config, addDeployment(botId, channelId))
    }
    if (method === 'delete') {
      const channelId = query.get('channelId')
      if (!channelId) throw buildError(config, 'Mock deployment delete requires channelId query', 400)
      removeDeployment(botId, channelId)
      return buildResponse(config, {}, 204)
    }
  }

  const templatesMatch = path.match(/^\/bots\/([^/]+)\/templates$/)
  if (templatesMatch && method === 'get') {
    return buildResponse(config, [])
  }

  const answersMatch = path.match(/^\/bots\/([^/]+)\/answers$/)
  if (answersMatch && method === 'get') {
    const botId = answersMatch[1]
    return buildResponse(config, listAnswers(botId))
  }
  if (answersMatch && method === 'post') {
    const botId = answersMatch[1]
    const created = createAnswer(botId, readPayload(config))
    return buildResponse(config, created, 201)
  }

  const answerDetailMatch = path.match(/^\/bots\/([^/]+)\/answers\/([^/]+)$/)
  if (answerDetailMatch && method === 'get') {
    const botId = answerDetailMatch[1]
    const answerId = answerDetailMatch[2]
    const answer = getAnswer(botId, answerId)
    if (!answer) throw buildError(config, `Mock answer not found: ${answerId}`, 404)
    return buildResponse(config, answer)
  }
  if (answerDetailMatch && method === 'patch') {
    const botId = answerDetailMatch[1]
    const answerId = answerDetailMatch[2]
    const payload = readPayload(config)
    const updated = updateAnswer(botId, answerId, payload)
    if (!updated) throw buildError(config, `Mock answer not found: ${answerId}`, 404)
    return buildResponse(config, updated)
  }

  const answerVariantsMatch = path.match(/^\/bots\/([^/]+)\/answers\/([^/]+)\/variants$/)
  if (answerVariantsMatch && method === 'get') {
    const botId = answerVariantsMatch[1]
    const answerId = answerVariantsMatch[2]
    return buildResponse(config, listAnswerVariants(botId, answerId))
  }

  const answerReferencedConversationsMatch = path.match(/^\/bots\/([^/]+)\/answers\/([^/]+)\/referenced-conversations$/)
  if (answerReferencedConversationsMatch && method === 'get') {
    return buildResponse(config, {})
  }

  const variablesMatch = path.match(/^\/bots\/([^/]+)\/variables$/)
  if (variablesMatch && method === 'put') {
    return buildResponse(config, { refs: readPayload(config) })
  }

  // PRO-671: bot-level AI agent settings.
  const agentSettingsMatch = path.match(/^\/bots\/([^/]+)\/agent-settings$/)
  if (agentSettingsMatch) {
    const bot = findBot(agentSettingsMatch[1])
    if (!bot) throw buildError(config, 'Bot not found', 404)

    const scopeKey = bot.multi_bot_id || bot.id
    if (method === 'put') {
      agentSettings[scopeKey] = Object.entries({ ...agentSettings[scopeKey], ...readPayload(config) })
        .reduce((kept, [key, value]) => (value ? { ...kept, [key]: value } : kept), {})
    }

    const settings = agentSettings[scopeKey] || {}
    return buildResponse(config, {
      bot_id: bot.id,
      multi_bot_id: bot.multi_bot_id || null,
      language: bot.language || null,
      settings,
      company_suggestion: 'Demo Organization s.r.o.',
      preview: renderAgentPreview(settings, bot.language, bot.type === 'email')
    })
  }

  return null
}

// Mirrors amio-chatbot-engine's agent-persona.prompt.js. It exists only so isolated mode can show a live
// preview with no engine behind it — the real text always comes from the engine, and this copy is not what
// any bot runs on. Keep the two in step when the wording changes.
function renderAgentPreview(settings, language, isEmail) {
  const value = key => (settings[`system_agent_${key}`] || '').trim()
  if (!['name', 'gender', 'role', 'company', 'formality', 'emoji', 'reply_length'].some(key => value(key))) return null

  const gender = value('gender') || 'female'
  const role = value('role') || (isEmail ? 'AI customer support assistant' : 'AI shopping assistant')
  const described = `${gender} ${role}`
  const subject = `${/^[aeiou]/i.test(described) ? 'an' : 'a'} ${described}`
  const forCompany = value('company') ? ` for ${value('company')}` : ''
  const persona = value('name') ?
    `You're ${value('name')}, ${subject}${forCompany}.` :
    `You're ${subject}${forCompany}.`

  // ponytail: Intl.DisplayNames needs Node 14+, CI runs Jest on Node 12 - fall back to the raw language code there
  const languageName = language ?
    (typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'language' }).of(language) : language) :
    null
  const languageRule = 'Always reply in the same language as the customer\'s actual message, regardless of the ' +
    'language used in retrieved documents.' + (languageName ? ` If the language is unclear, use ${languageName}.` : '')

  const tone = [
    { short: 'Keep replies short, clear, and helpful.', detailed: 'Keep replies thorough, explaining the reasoning behind your recommendations.' }[value('reply_length')],
    { none: 'Do not use emojis.', moderate: 'Use emojis naturally and in moderation.', frequent: 'Use emojis freely wherever they fit.' }[value('emoji')],
    { formal: 'Address customers formally.', informal: 'Address customers informally.' }[value('formality')]
  ].filter(Boolean).map(line => `- ${line}`).join('\n')

  const rules = [
    '- Never promise actions outside answering questions.',
    value('company') ?
      `- Do not mention competitors or direct the customer to manufacturer websites or outside ${value('company')}.` :
      '- Do not mention competitors or direct the customer to manufacturer websites.',
    '- Never respond with only showing the products.'
  ].join('\n')

  return [persona, languageRule, tone, rules].filter(Boolean).join('\n\n')
}

function handleCommunicatorApi(config) {
  throw buildError(config, 'Communicator mock route not implemented yet', 501)
}

export function createMockAdapter(config, clientName) {
  const method = (config.method || 'get').toLowerCase()
  const { path, query: urlQuery } = parseUrlParts(config.url || '')
  const query = mergeQueryWithConfigParams(urlQuery, config.params)
  const strict = isStrictApiMockingEnabled()

  let response = null
  if (clientName === 'org') response = handleOrgApi(config, method, path, query)
  if (clientName === 'engine') response = handleEngineApi(config, method, path, query)
  if (clientName === 'communicator') response = handleCommunicatorApi(config, method, path, query)

  if (response) return response
  if (!strict) return null

  throw buildError(
    config,
    `Missing API mock for [${clientName}] ${method.toUpperCase()} ${path}`,
    501
  )
}
